/**
 * RunReportButton — dropdown button used on profile Reporting tabs.
 *
 * Lets the user kick off a detailed Alert Report scoped to the current entity
 * (resident / area / device / etc.) over Yesterday, the last 7 days, or the
 * last 28 days. Navigates to /reports/alerts/run with start/end dates and the
 * supplied filter context in router state.
 *
 * Props:
 *   filters {Object} — pre-applied report filters, e.g. { residents: [123] }
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToday } from '../../libs.jsx';
/* Its own stylesheets (audit C12). index.jsx already loads these first, so this does not
   move them in the cascade; it states the dependency, so the component stays styled
   wherever it is reused. */
import '../../styles/dp-primitives.css';

const REPORT_RANGES = [
    { key: 'yesterday',  label: 'Yesterday',    daysBack: 1 },
    { key: 'last7days',  label: 'Last 7 Days',  daysBack: 7 },
    { key: 'last28days', label: 'Last 28 Days', daysBack: 28 },
];

const MenuItem = ({ label, onClick }) => {
    const [hover, setHover] = useState(false);
    return (
        <button
            role="menuitem"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-body)',
                background: hover ? 'var(--color-bg-hover, rgba(0,0,0,0.06))' : 'transparent',
                border: 'none',
                cursor: 'pointer',
            }}
        >
            {label}
        </button>
    );
};

const RunReportButton = ({ filters }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const handlePick = (range) => {
        setOpen(false);
        const start = getToday(-range.daysBack);
        start.setHours(0, 0, 0, 0);
        const end = getToday(-1);
        end.setHours(23, 59, 59, 999);
        navigate('/reports/alerts/run', {
            state: {
                filters: filters || {},
                startDate: start.toISOString(),
                endDate: end.toISOString(),
            },
        });
    };

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                className="dp-btn dp-btn--secondary dp-btn--sm"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                Run Detailed Report
                <i className="fe fe-chevron-down" style={{ marginLeft: '6px' }} />
            </button>
            {open ? (
                <div
                    role="menu"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        zIndex: 100,
                        minWidth: '160px',
                        background: 'var(--color-bg-surface)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        overflow: 'hidden',
                    }}
                >
                    {REPORT_RANGES.map((r) => (
                        <MenuItem key={r.key} label={r.label} onClick={() => handlePick(r)} />
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default RunReportButton;
